import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signUpSchema } from '../schemas/signUpSchema';
import { register as registerUser } from '../api/auth';
import { getCompanyId } from '../utils/storage';
import { NoticeModal } from '../components/Modals';
import {
  Box,
  Button,
  Typography,
  Alert,
  Link,
  FormControlLabel,
  Checkbox,
  TextField,
  Container,
  Paper,
  Divider,
} from '@mui/material';

const SignUpPage = () => {
  const navigate = useNavigate();
  const companyId = getCompanyId();
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
    watch,
  } = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      agree: false,
    },
  });

  const agreeValue = watch('agree');

  useEffect(() => {
    if (!companyId) {
      setFormError('root', {
        type: 'manual',
        message: '正しいURLから再度アクセスしてください。',
      });
    }
  }, [companyId, setFormError]);

  const onSubmit = async (data) => {
    const price_id = localStorage.getItem('campaignCode');
    try {
      const res = await registerUser(
        data.name,
        data.phoneNumber,
        data.email,
        data.password,
        data.passwordConfirmation,
        companyId,
        price_id
      );

      if (res.success) {
        setSuccessModalOpen(true);
      } else {
        setFormError('root', {
          type: 'manual',
          message: res.message || '登録に失敗しました',
        });
      }
    } catch (error) {
      if (error.response && error.response.data.errors) {
        const errors = error.response.data.errors;
        let errorMessage = '';
        for (let field in errors) {
          if (field === 'company_id' || field === 'price_id') {
            errorMessage = '正しいURLから再度アクセスしてください';
            break;
          }
          errorMessage += `${errors[field]}\n`;
        }
        setFormError('root', {
          type: 'manual',
          message: errorMessage,
        });
      } else {
        setFormError('root', {
          type: 'manual',
          message: '通信エラー',
        });
      }
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModalOpen(false);
    navigate('/login');
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        py: { xs: '7vh', sm: '10vh', md: '10vh', lg: '10vh' },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 3,
          marginBottom: '2vh',
        }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{
            margin: 2,
          }}
        >
          新規登録
        </Typography>

        <Divider
          sx={{
            width: '100%',
            borderColor: '#32c86b',
            borderBottomWidth: 3,
          }}
        />

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            maxWidth: 'none',
            boxShadow: 'none',
            minHeight: 'fit-content',
            padding: 0,
            mt: '5dvh',
          }}
        >
          <TextField
            label="名前"
            id="name"
            name="name"
            type="text"
            autoComplete="name"
            fullWidth
            placeholder="名前を入力してください"
            {...register('name')}
            error={!!errors.name}
            helperText={errors.name?.message}
          /><br/>

          <TextField
            label="電話番号"
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            autoComplete="tel"
            fullWidth
            placeholder="電話番号を入力してください"
            {...register('phoneNumber')}
            error={errors.phoneNumber}
            helperText={errors.phoneNumber?.message}
          /><br/>

          <TextField
            label="メールアドレス"
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            fullWidth
            placeholder="xxx@example.com"
            {...register('email')}
            error={errors.email}
            helperText={errors.email?.message}
          /><br/>

          <TextField
            label="パスワード"
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            fullWidth
            placeholder="パスワードを入力してください"
            {...register('password')}
            error={errors.password}
            helperText={errors.password?.message}
          /><br/>

          <TextField
            label="確認用パスワード"
            id="passwordConfirmation"
            name="passwordConfirmation"
            type="password"
            autoComplete="new-password"
            fullWidth
            placeholder="確認用パスワードを入力してください"
            {...register('passwordConfirmation')}
            error={errors.passwordConfirmation}
            helperText={errors.passwordConfirmation?.message}
          />

          <FormControlLabel
            control={
              <Checkbox
                {...register('agree')}
                checked={agreeValue}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                <Link
                  href="https://stripe.com/jp/legal"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  Stripeの利用規約
                </Link>

                と
                
                <Link
                  href="https://stripe.com/jp/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  プライバシーポリシー
                </Link>
                に同意します
              </Typography>
            }
            sx={{
              mt: 2,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          />
          {errors.agree && (
            <Typography
              color="error"
              variant="caption"
              sx={{ mt: 1, display: 'block' }}
            >
              {errors.agree.message}
            </Typography>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2, borderRadius: 5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? '登録中...' : '登録'}
          </Button>

          <Box sx={{ marginTop: 2 }}>
            <Typography variant="body2" align="center">
              すでにアカウントをお持ちですか？{' '}
              <Link
                variant="body2"
                sx={{
                  color: '#007bff',
                  textDecoration: 'none',
                  '&:hover':{
                    color:'#007bffc1',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                ログイン
              </Link>
            </Typography>
          </Box>
          {errors.root && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.root.message}
            </Alert>
          )}
          {/* メール受信設定の案内 */}
          <Alert
            severity="info"
            sx={{
              padding: 1,
              mt:2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                marginBottom: 2,
                color: '#495057',
                fontWeight: 'bold',
              }}
            >
              メール受信設定について
            </Typography>

            <Typography
              variant="body2"
              sx={{
                marginBottom: 2,
                color: '#6c757d',
                lineHeight: 1.6,
              }}
            >
              お客様にスムーズにご連絡を差し上げるため、以下の内容をご確認いただき、必要に応じて迷惑メール設定の変更をお願いいたします。
            </Typography>

            <Box sx={{ marginBottom: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  marginBottom: 1,
                  color: '#495057',
                  fontWeight: 'bold',
                }}
              >
                受信許可リストへの追加
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  marginBottom: 1,
                  color: '#6c757d',
                }}
              >
                下記のドメインを受信許可リストに追加してください
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: '#e9ecef',
                  padding: 1,
                  borderRadius: 1,
                  color: '#495057',
                }}
              >
                {process.env.NODE_ENV === 'production'
                  ? '@serial-payment.com'
                  : '@stg.serial-payment.com'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  marginBottom: 1,
                  color: '#495057',
                  fontWeight: 'bold',
                }}
              >
                迷惑メールフォルダの確認
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6c757d',
                  lineHeight: 1.6,
                }}
              >
                万が一、当社からのメールが届かない場合は、迷惑メールフォルダに振り分けられている可能性がございます。フォルダをご確認の上、該当メールを「迷惑メールではない」と設定してください。
              </Typography>
            </Box>
          </Alert>
        </Box>
      </Paper>

      <NoticeModal
        open={successModalOpen}
        message="会員登録が完了しました。ログイン画面に移動します。"
        onClose={handleSuccessModalClose}
      />
    </Container>
  );
};

export default SignUpPage;
